import React, { useState, useEffect } from 'react';
import { Col, Statistic, Empty, Progress, DatePicker, Select, Button, message, Switch } from 'antd';
import HeartLoader from '../../../components/shared/HeartLoader';
import {
  UserOutlined,
  EnvironmentOutlined,
  HeartOutlined,
  ReloadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { usePageHeader } from '../../../layouts/AdminLayout';
import {
  StyledAnalyticsTabs,
  TabsAndFiltersRow,
  FiltersContainer,
  ChartCard,
  StatCard,
  StatsRow,
  ChartsRow,
  PageContainer,
  selectStyles,
  rangePickerStyles,
  valueColors,
  suffixStyles,
  iconColors
} from '../styled';
import { CategoryLabel, marginStyles } from './styled';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Population = ({ dateRange: propDateRange, timeRange: propTimeRange }) => {
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('demographics');
  const [dateRange, setDateRange] = useState(propDateRange || [dayjs().subtract(365, 'days'), dayjs()]);
  const [quickRange, setQuickRange] = useState(propTimeRange || 'year');
  
  const [demographicsData, setDemographicsData] = useState(null);
  const [retentionData, setRetentionData] = useState(null);
  const [geographicData, setGeographicData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [excludeArchived, setExcludeArchived] = useState(true);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD')
      };
      const response = await api.getBlob('/screening/exports/demographics', params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `demographics-${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Demographics data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export demographics data');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setPageHeader({ title: 'Population Analytics', count: null, countIcon: null });
  }, [setPageHeader]);

  useEffect(() => {
    loadData();
  }, [dateRange, excludeArchived]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        timeRange: quickRange,
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        ...(excludeArchived && { excludeArchived: 'true' })
      };

      const [demoRes, retRes, geoRes, riskRes] = await Promise.all([
        api.get('/screening/analytics/population/demographics', params),
        api.get('/screening/analytics/population/retention', params),
        api.get('/screening/analytics/population/geographic', params),
        api.get('/screening/analytics/population/risk-stratification', params)
      ]);

      const extractData = (res) => res.data?.success !== undefined 
        ? (res.data.success ? res.data.data : null) 
        : res.data;

      if (extractData(demoRes)) setDemographicsData(extractData(demoRes));
      if (extractData(retRes)) setRetentionData(extractData(retRes));
      if (extractData(geoRes)) setGeographicData(extractData(geoRes));
      if (extractData(riskRes)) setRiskData(extractData(riskRes));
    } catch (error) {
      console.error('Error loading population data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderDemographics = () => {
    if (!demographicsData) return <Empty description="No data available" />;

    const genderColors = { 'Male': '#1890ff', 'Female': '#eb2f96', 'Other': '#722ed1', 'Unknown': '#8c8c8c' };

    // Gender pie chart
    const genderOptions = {
      chart: { type: 'donut' },
      labels: demographicsData.genderDistribution?.map(g => g.gender) || [],
      colors: demographicsData.genderDistribution?.map(g => genderColors[g.gender] || '#8c8c8c') || [],
      legend: { position: 'bottom' },
      dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(1)}%` }
    };
    const genderSeries = demographicsData.genderDistribution?.map(g => g.count) || [];

    // Age bar chart
    const ageOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      colors: ['#722ed1'],
      xaxis: { categories: demographicsData.ageDistribution?.map(a => a.ageRange) || [] },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: (val) => `${val} patients` } }
    };
    const ageSeries = [{ name: 'Patients', data: demographicsData.ageDistribution?.map(a => a.count) || [] }];

    // Ethnicity horizontal bar
    const ethOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      colors: ['#13c2c2'],
      xaxis: { categories: demographicsData.ethnicityDistribution?.slice(0, 10).map(e => e.ethnicity) || [] },
      dataLabels: { enabled: false }
    };
    const ethSeries = [{ name: 'Patients', data: demographicsData.ethnicityDistribution?.slice(0, 10).map(e => e.count) || [] }];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={24} lg={8}>
            <StatCard>
              <Statistic title="Total Patients" value={demographicsData.summary.totalPatients} prefix={<UserOutlined style={iconColors.primary} />} />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Gender Distribution">
              <Chart options={genderOptions} series={genderSeries} type="donut" height={300} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Age Distribution">
              <Chart options={ageOptions} series={ageSeries} type="bar" height={300} />
            </ChartCard>
          </Col>
        </ChartsRow>

        <ChartCard title="Ethnicity Distribution">
          <Chart options={ethOptions} series={ethSeries} type="bar" height={300} />
        </ChartCard>
      </>
    );
  };

  const renderRetention = () => {
    if (!retentionData) return <Empty description="No data available" />;

    // Frequency pie chart
    const freqOptions = {
      chart: { type: 'donut' },
      labels: retentionData.frequencyDistribution?.map(f => f.range) || [],
      colors: ['#1890ff', '#108043', '#722ed1', '#fa8c16'],
      legend: { position: 'bottom' },
      dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(1)}%` }
    };
    const freqSeries = retentionData.frequencyDistribution?.map(f => f.count) || [];

    // Acquisition trend line chart
    const trendOptions = {
      chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { curve: 'smooth', width: 3 },
      colors: ['#108043'],
      xaxis: { categories: retentionData.acquisitionTrend?.map(t => t.month) || [] },
      markers: { size: 4 },
      tooltip: { y: { formatter: (val) => `${val} patients` } }
    };
    const trendSeries = [{ name: 'New Patients', data: retentionData.acquisitionTrend?.map(t => t.newPatients) || [] }];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Total Patients" value={retentionData.summary.totalPatients} prefix={<UserOutlined style={iconColors.primary} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="New Patients" value={retentionData.summary.newPatients} prefix={<UserOutlined style={iconColors.success} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Returning Patients" value={retentionData.summary.returningPatients} prefix={<ReloadOutlined style={iconColors.purple} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Retention Rate" value={retentionData.summary.retentionRate} suffix="%" valueStyle={{ color: parseFloat(retentionData.summary.retentionRate) > 30 ? '#108043' : '#faad14' }} />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Visit Frequency">
              <Chart options={freqOptions} series={freqSeries} type="donut" height={300} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Patient Acquisition Trend">
              <Chart options={trendOptions} series={trendSeries} type="line" height={300} />
            </ChartCard>
          </Col>
        </ChartsRow>
      </>
    );
  };

  const renderGeographic = () => {
    if (!geographicData) return <Empty description="No data available" />;

    // State bar chart
    const stateOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      colors: ['#1890ff'],
      xaxis: { categories: geographicData.stateDistribution?.slice(0, 15).map(s => s.state) || [] },
      dataLabels: { enabled: false }
    };
    const stateSeries = [{ name: 'Patients', data: geographicData.stateDistribution?.slice(0, 15).map(s => s.count) || [] }];

    // City bar chart
    const cityOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      colors: ['#108043'],
      xaxis: { categories: geographicData.cityDistribution?.map(c => c.city) || [] },
      dataLabels: { enabled: false }
    };
    const citySeries = [{ name: 'Patients', data: geographicData.cityDistribution?.map(c => c.count) || [] }];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic title="Total Patients" value={geographicData.summary.totalPatients} prefix={<UserOutlined style={iconColors.primary} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic title="States Reached" value={geographicData.summary.statesReached} prefix={<EnvironmentOutlined style={iconColors.success} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic title="Cities Reached" value={geographicData.summary.citiesReached} prefix={<EnvironmentOutlined style={iconColors.purple} />} />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Patients by State">
              <Chart options={stateOptions} series={stateSeries} type="bar" height={400} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Top 10 Cities">
              <Chart options={cityOptions} series={citySeries} type="bar" height={400} />
            </ChartCard>
          </Col>
        </ChartsRow>
      </>
    );
  };

  const renderRisk = () => {
    if (!riskData) return <Empty description="No data available" />;

    // Risk distribution pie chart
    const riskPieOptions = {
      chart: { type: 'donut' },
      labels: riskData.riskDistribution?.map(r => r.level) || [],
      colors: riskData.riskDistribution?.map(r => r.color) || ['#108043', '#faad14', '#ff7a45', '#b91c1c'],
      legend: { position: 'bottom' },
      dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(1)}%` }
    };
    const riskPieSeries = riskData.riskDistribution?.map(r => r.count) || [];

    // Risk by gender stacked bar
    const genderRiskOptions = {
      chart: { type: 'bar', toolbar: { show: false }, stacked: true },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      colors: ['#108043', '#faad14', '#ff7a45', '#b91c1c'],
      xaxis: { categories: riskData.riskByGender?.map(g => g.gender) || [] },
      legend: { position: 'top' },
      dataLabels: { enabled: false }
    };
    const genderRiskSeries = [
      { name: 'Low Risk', data: riskData.riskByGender?.map(g => g.low) || [] },
      { name: 'Moderate', data: riskData.riskByGender?.map(g => g.moderate) || [] },
      { name: 'High Risk', data: riskData.riskByGender?.map(g => g.high) || [] },
      { name: 'Critical', data: riskData.riskByGender?.map(g => g.critical) || [] }
    ];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Total Patients" value={riskData.summary.totalPatients} prefix={<UserOutlined style={iconColors.primary} />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="Assessed" value={riskData.summary.assessedPatients} suffix={<span style={suffixStyles}>{riskData.summary.assessmentCoverage}%</span>} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="At Risk" value={riskData.summary.atRiskPatients} valueStyle={{ color: valueColors.danger }} prefix={<HeartOutlined />} />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic title="At Risk Rate" value={riskData.summary.atRiskPercentage} suffix="%" valueStyle={{ color: parseFloat(riskData.summary.atRiskPercentage) > 20 ? '#b91c1c' : '#108043' }} />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Risk Level Distribution">
              <Chart options={riskPieOptions} series={riskPieSeries} type="donut" height={350} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Risk Breakdown">
              {riskData.riskDistribution?.map((risk, idx) => (
                <div key={idx} style={marginStyles.mb16}>
                  <CategoryLabel>
                    <span className="label">{risk.level}</span>
                    <span className="value">{risk.count} ({risk.percentage}%)</span>
                  </CategoryLabel>
                  <Progress percent={parseFloat(risk.percentage)} strokeColor={risk.color} showInfo={false} />
                </div>
              ))}
            </ChartCard>
          </Col>
        </ChartsRow>

        {riskData.riskByGender?.length > 0 && (
          <ChartCard title="Risk by Gender">
            <Chart options={genderRiskOptions} series={genderRiskSeries} type="bar" height={300} />
          </ChartCard>
        )}
      </>
    );
  };

  const subTabItems = [
    { key: 'demographics', label: 'Demographics' },
    { key: 'retention', label: 'New vs Returning' },
    { key: 'geographic', label: 'Geographic' },
    { key: 'risk', label: 'Risk Stratification' }
  ];

  const renderContent = () => {
    switch (activeSubTab) {
      case 'retention': return renderRetention();
      case 'geographic': return renderGeographic();
      case 'risk': return renderRisk();
      default: return renderDemographics();
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
      setQuickRange('custom');
    }
  };

  const handleQuickRangeChange = (value) => {
    setQuickRange(value);
    const now = dayjs();
    let newRange;
    switch (value) {
      case '30days': newRange = [now.subtract(30, 'days'), now]; break;
      case '3months': newRange = [now.subtract(3, 'months'), now]; break;
      case '6months': newRange = [now.subtract(6, 'months'), now]; break;
      case 'year': newRange = [now.subtract(1, 'year'), now]; break;
      case 'Q1': newRange = [dayjs().startOf('year'), dayjs().startOf('year').month(2).endOf('month')]; break;
      case 'Q2': newRange = [dayjs().startOf('year').month(3), dayjs().startOf('year').month(5).endOf('month')]; break;
      case 'Q3': newRange = [dayjs().startOf('year').month(6), dayjs().startOf('year').month(8).endOf('month')]; break;
      case 'Q4': newRange = [dayjs().startOf('year').month(9), dayjs().startOf('year').month(11).endOf('month')]; break;
      default: return;
    }
    setDateRange(newRange);
  };

  if (loading && !demographicsData) {
    return (
      <PageContainer>
        <HeartLoader size="48px" fullHeight />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <TabsAndFiltersRow className="filters-animate-in">
        <StyledAnalyticsTabs activeKey={activeSubTab} onChange={setActiveSubTab} items={subTabItems} />
        <FiltersContainer>
          <Select value={quickRange} onChange={handleQuickRangeChange} style={selectStyles} size="large">
            <Option value="30days">Last 30 days</Option>
            <Option value="3months">Last 3 months</Option>
            <Option value="6months">Last 6 months</Option>
            <Option value="year">Last year</Option>
            <Option value="Q1">Q1 (Jan-Mar)</Option>
            <Option value="Q2">Q2 (Apr-Jun)</Option>
            <Option value="Q3">Q3 (Jul-Sep)</Option>
            <Option value="Q4">Q4 (Oct-Dec)</Option>
            <Option value="custom">Custom</Option>
          </Select>
          <RangePicker value={dateRange} onChange={handleDateRangeChange} format="MM/DD/YY" style={rangePickerStyles} size="large" allowClear={false} />
          <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting} size="large">Export</Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch size="small" checked={excludeArchived} onChange={setExcludeArchived} />
            <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Exclude Archived</span>
          </div>
        </FiltersContainer>
      </TabsAndFiltersRow>

      {loading ? <HeartLoader size="32px" padding="40px" /> : <div className="content-animate-in">{renderContent()}</div>}
    </PageContainer>
  );
};

export default Population;
